import { normalisePlainText } from "authoring/routes/project/amends/lib/normalise-plain-text.mjs"
import { PAYLOAD_STATUS_APPROVED, PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { CURRENT_CYCLE_EXCLUDED_AMENDS_KEYS } from "./current-cycle-excluded-amends-keys.js"
import { hasAudienceAmendsContent } from "../../amends/lib/audience-aware-amends.mjs"
import { isLegislationAmendsKey } from "../../amends/lib/constants/legislation-amends-keys.mjs"
import { getTaskIdFromAmendsKey } from "../../amends/lib/task-amends-key.js"
import { LEADING_SUMMARY_AMENDS_KEY, isLeadingSummaryTaskId } from "../../amends/lib/constants/amends-keys"

export function buildAuthoringAuthorNotesView({ records = [], currentReviewCycle, scheduleId, draftTasks = [] }) {
    const taskTitleLookup = buildTaskTitleLookup(draftTasks)
    const cycleGroups = buildCycleGroups(records, scheduleId, taskTitleLookup, currentReviewCycle)
    const currentCycleGroup = cycleGroups.find((group) => group.reviewCycle === currentReviewCycle) ?? null

    return {
        currentCycleRejection: currentCycleGroup?.rejection ?? null,
        currentCycleCounts: currentCycleGroup?.decisionCounts ?? { approved: 0, rejected: 0 },
        cycleGroups,
    }
}

export function getAuthoringNoteLabel(record) {
    const amendsKey = getAmendsKey(record)
    if (!amendsKey) return "Author Note"

    if (amendsKey === LEADING_SUMMARY_AMENDS_KEY) return "Leading Summary"
    if (amendsKey === "intro.content") return "Author Note"
    if (amendsKey === "intro.note") return "Author Note"
    if (amendsKey === "title") return "Schedule Title Note"
    if (amendsKey === "code") return "Schedule Code Note"
    if (amendsKey === "unitOfMeasure") return "Schedule Unit of Measure Note"
    if (amendsKey === "movements") return "Task Order Note"
    if (isLegislationAmendsKey(amendsKey)) return "Reference Change"
    if (amendsKey.endsWith(".content")) return "Task Content Note"
    if (amendsKey.endsWith(".note")) return "Task Note"
    if (amendsKey.endsWith(".title")) return "Task Title Note"

    return "Author Note"
}

function buildCycleGroups(records, scheduleId, taskTitleLookup, currentReviewCycle) {
    const groupsByCycle = new Map()
    const orderedRecords = [...records].sort(sortByDateDesc)

    for (const record of orderedRecords) {
        const reviewCycle = getReviewCycle(record)
        if (reviewCycle == null) continue

        if (!groupsByCycle.has(reviewCycle)) {
            groupsByCycle.set(reviewCycle, [])
        }

        groupsByCycle.get(reviewCycle).push(record)
    }

    return Array.from(groupsByCycle.entries())
        .sort((a, b) => b[0] - a[0])
        .map(([reviewCycle, groupRecords]) =>
            buildCycleGroup({
                reviewCycle,
                records: groupRecords,
                scheduleId,
                taskTitleLookup,
                isCurrentCycle: reviewCycle === currentReviewCycle,
            })
        )
}

function buildCycleGroup({ reviewCycle, records, scheduleId, taskTitleLookup, isCurrentCycle }) {
    const decisions = records.filter(isDecisionRecord)
    const decisionsByKey = new Map()

    for (const decision of decisions) {
        const decisionKey = getDecisionGroupKey(decision, scheduleId)
        if (!decisionKey || decisionsByKey.has(decisionKey)) continue
        decisionsByKey.set(decisionKey, decision)
    }

    const genericLegislationDecision = findGenericLegislationDecision(decisionsByKey)

    const entriesByKey = new Map()

    for (const record of records) {
        if (!isChangeAmendRecord(record)) continue

        const entryKey = getAmendGroupKey(record)
        if (!entryKey) continue

        if (!entriesByKey.has(entryKey)) {
            const decision =
                decisionsByKey.get(entryKey) ?? (isLegislationAmendsKey(entryKey) ? genericLegislationDecision : null)
            entriesByKey.set(entryKey, {
                key: entryKey,
                amends: [],
                decision,
                latestTimestamp: 0,
            })
        }

        const entry = entriesByKey.get(entryKey)
        entry.amends.push(record)
        entry.latestTimestamp = Math.max(entry.latestTimestamp, getTimestamp(record))
    }

    const entries = Array.from(entriesByKey.values())
        .map((entry) => normaliseEntry(entry, taskTitleLookup, isCurrentCycle))
        .sort((a, b) => b.latestTimestamp - a.latestTimestamp)

    return {
        reviewCycle,
        rejection: records.find(isDraftRejectedRecord) ?? null,
        returnedToEditing: records.find(isDraftReturnedToEditingRecord) ?? null,
        decisionCounts: {
            approved: decisions.filter((record) => record?.payload?.status === PAYLOAD_STATUS_APPROVED).length,
            rejected: decisions.filter((record) => record?.payload?.status === PAYLOAD_STATUS_REJECTED).length,
        },
        entries,
    }
}

function isChangeAmendRecord(record) {
    if (isDecisionRecord(record)) return false

    const amendsKey = getAmendsKey(record)
    if (CURRENT_CYCLE_EXCLUDED_AMENDS_KEYS.has(amendsKey)) return false

    if (hasAudienceAmendsContent(record?.payload ?? {})) return true
    if (isLegislationAmendsKey(amendsKey) && record?.payload?.changeType) return true

    return false
}

function isDraftRejectedRecord(record) {
    return getAmendsKey(record) === "draft.rejected"
}

function isDraftReturnedToEditingRecord(record) {
    return getAmendsKey(record) === "draft.returnedToEditing"
}

function isDecisionRecord(record) {
    const status = record?.payload?.status
    return (
        Boolean(record?.payload?.groupReviewId) &&
        (status === PAYLOAD_STATUS_APPROVED || status === PAYLOAD_STATUS_REJECTED)
    )
}

function getAmendGroupKey(record) {
    const amendsKey = getAmendsKey(record)
    if (!amendsKey) return null

    if (amendsKey === LEADING_SUMMARY_AMENDS_KEY) {
        return LEADING_SUMMARY_AMENDS_KEY
    }

    if (amendsKey.startsWith("tasks.")) {
        return `task:${getTaskId(amendsKey)}`
    }

    if (amendsKey === "intro.content") {
        return "intro.content"
    }
    if (amendsKey === "intro.note") {
        return "intro.note"
    }

    if (amendsKey === "movements") return "movements"

    if (amendsKey === "title" || amendsKey === "code" || amendsKey === "unitOfMeasure") {
        return `details:${amendsKey}`
    }

    return amendsKey
}

function getDecisionGroupKey(record, scheduleId) {
    const taskId = record?.payload?.taskId
    const taskTitle = record?.payload?.taskTitle
    if (!taskId) return null

    if (taskTitle === "intro.content" || taskId === `${scheduleId}-intro.content`) {
        return "intro.content"
    }

    if (taskTitle === "intro.note" || taskId === `${scheduleId}-intro.note`) {
        return "intro.note"
    }

    if (taskTitle === LEADING_SUMMARY_AMENDS_KEY || isLeadingSummaryTaskId(taskId, scheduleId)) {
        return LEADING_SUMMARY_AMENDS_KEY
    }

    const detailsTaskId = `${scheduleId}-details`
    if (taskId === `${scheduleId}-movements`) return "movements"

    if (taskId === detailsTaskId) {
        return "details:title"
    }

    const detailsPrefix = `${scheduleId}-details:`
    if (taskId.startsWith(detailsPrefix)) {
        return `details:${taskId.slice(detailsPrefix.length)}`
    }

    const legislationPrefix = `${scheduleId}-legislation.`
    if (taskId.startsWith(legislationPrefix)) {
        return taskId.slice(`${scheduleId}-`.length)
    }

    return `task:${taskId}`
}

function getReviewCycle(record) {
    const reviewCycle = Number(record?.payload?.reviewCycle ?? record?.referenceId?.split("!").at(1))
    return Number.isInteger(reviewCycle) ? reviewCycle : null
}

function getAmendsKey(record) {
    return record?.payload?.amendsKey ?? record?.payload?.field ?? null
}

function getTaskId(amendsKey = "") {
    return getTaskIdFromAmendsKey(amendsKey)
}

function normaliseEntry(entry, taskTitleLookup, isCurrentCycle) {
    const sortedAmends = [...entry.amends].sort(sortByDateDesc)
    const primaryAmend = sortedAmends[0] ?? null
    return {
        ...entry,
        amends: sortedAmends,
        primaryAmend,
        displayTaskTitle: resolveDisplayTaskTitle(entry?.key, primaryAmend, taskTitleLookup, isCurrentCycle),
    }
}

function buildTaskTitleLookup(draftTasks = []) {
    const lookup = new Map()
    let taskIndex = 1

    for (const task of draftTasks) {
        const taskId = task?.taskId ?? task?.id
        if (!taskId) continue
        if (task?.isHeader) continue

        const title = normalisePlainText(task?.title) || null
        lookup.set(taskId, title ? `Task ${taskIndex}. ${title}` : `Task ${taskIndex}`)
        taskIndex++
    }

    return lookup
}

function resolveDisplayTaskTitle(entryKey, primaryAmend, taskTitleLookup, isCurrentCycle) {
    if (entryKey === LEADING_SUMMARY_AMENDS_KEY) return "Leading Summary"

    const storedTaskTitle = normalisePlainText(primaryAmend?.payload?.taskTitle) || null

    // Previous cycles are a fixed historical record: use the title/position snapshot stored on the
    // amend at the time the cycle was completed. Only the current cycle tracks live draft edits.
    if (!isCurrentCycle) return storedTaskTitle

    const taskId = getTaskIdFromEntryKey(entryKey)
    if (!taskId) return storedTaskTitle

    return taskTitleLookup.get(taskId) ?? storedTaskTitle
}

function getTaskIdFromEntryKey(entryKey = "") {
    return entryKey.startsWith("task:") ? entryKey.slice("task:".length) : null
}

function getTimestamp(record) {
    const value = new Date(record?.date ?? 0).getTime()
    return Number.isNaN(value) ? 0 : value
}

function getGroupSequence(record) {
    const value = record?.payload?.groupSequence
    return Number.isInteger(value) ? value : null
}

function sortByDateDesc(a, b) {
    const timeDiff = getTimestamp(b) - getTimestamp(a)
    if (timeDiff !== 0) return timeDiff

    return sortByGroupSequenceDesc(a, b)
}

function sortByGroupSequenceDesc(a, b) {
    const sequenceA = getGroupSequence(a)
    const sequenceB = getGroupSequence(b)
    if (sequenceA === null || sequenceB === null) return 0
    return sequenceB - sequenceA
}

function findGenericLegislationDecision(decisionsByKey) {
    for (const [decisionKey, decision] of decisionsByKey) {
        if (
            decisionKey === "task:Guidance" ||
            decisionKey === "task:References" ||
            decisionKey.endsWith("-Guidance") ||
            decisionKey.endsWith("-References")
        ) {
            return decision
        }
    }
    return null
}
